import type { SessionAnswerValue, SessionApp, SessionFileAnswer, SessionKeyAnswer, SessionPurpose, SessionQuestion, SessionQuestionType } from '../../../types/aai';

export const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Owner',
  EXECUTIVE: 'Executive',
  AI_CHAMPION: 'AI Champion',
  DEPARTMENT_HEAD: 'Department head',
  TEAM_LEAD: 'Team lead',
  AI_PARTICIPANT: 'Employee',
  TRAINER: 'Trainer',
  EXPERT: 'Expert',
};

export type RoleGroup = 'all' | 'owners' | 'heads' | 'employees';

export const ROLE_GROUPS: { key: RoleGroup; label: string; roles: string[] }[] = [
  { key: 'all', label: 'Everyone', roles: [] },
  { key: 'owners', label: 'Owners and Champions', roles: ['OWNER', 'EXECUTIVE', 'AI_CHAMPION'] },
  { key: 'heads', label: 'Department heads', roles: ['DEPARTMENT_HEAD', 'TEAM_LEAD'] },
  { key: 'employees', label: 'Employees', roles: ['AI_PARTICIPANT'] },
];

export const roleLabel = (role: string): string => ROLE_LABEL[(role || '').toUpperCase()] || role || 'Member';

export const inGroup = (role: string, group: RoleGroup): boolean => {
  if (group === 'all') return true;
  const spec = ROLE_GROUPS.find((g) => g.key === group);
  if (!spec) return true;
  return spec.roles.includes((role || '').toUpperCase());
};

export const matches = (query: string, ...fields: (string | null | undefined)[]): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => (f || '').toLowerCase().includes(q));
};

export const TYPE_LABEL: Record<SessionQuestionType, string> = {
  text: 'Open answer',
  single: 'One choice',
  multi: 'Several choices',
  scale: 'Scale 1 to 5',
  tools: 'Pick apps (logo wall)',
  file: 'Upload a document',
  secret: 'Secret key (to the Vault)',
};

export const PURPOSE_LABEL: Record<SessionPurpose, string> = {
  project: 'A project',
  quick_win: 'A Quick Win',
  general: 'General questions',
  context: 'Company context',
};

export const REMIND_CHOICES: { value: number | null; label: string }[] = [
  { value: null, label: 'No reminder' },
  { value: 1, label: 'Every day' },
  { value: 2, label: 'Every 2 days' },
  { value: 3, label: 'Every 3 days' },
  { value: 7, label: 'Every week' },
];

/* A key answer: never the value, only what the server keeps about it. */
export const asKey = (value: SessionAnswerValue | undefined): SessionKeyAnswer | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value) || !('length' in value)) return null;
  return value as SessionKeyAnswer;
};

export const maskedKey = (key: SessionKeyAnswer): string => `${key.hint}\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022 (${key.length} characters)`;

export const KIND_LABEL: Record<string, string> = {
  questionnaire: 'Questionnaire',
  survey: 'Survey',
};

export const answerText = (q: SessionQuestion, value: SessionAnswerValue | undefined): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return [...asStrings(value), ...asApps(value).map((a) => a.label), ...asFiles(value).map((f) => f.name)].join(', ');
  if (q.type === 'scale') return `${value} of 5`;
  const key = asKey(value);
  if (key) return maskedKey(key);
  return String(value);
};

/* An answer array holds option labels for a choice question, apps for a
   tools question and documents for a file question; these read each kind out of it. */
export const asApps = (value: SessionAnswerValue | undefined): SessionApp[] => {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter((v): v is SessionApp => typeof v === 'object' && v !== null && 'slug' in v);
};

export const asFiles = (value: SessionAnswerValue | undefined): SessionFileAnswer[] => {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter((v): v is SessionFileAnswer => typeof v === 'object' && v !== null && 'name' in v && 'id' in v);
};

export const asStrings = (value: SessionAnswerValue | undefined): string[] => {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter((v): v is string => typeof v === 'string');
};

export const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
};

export const plural = (n: number, one: string, many: string): string => {
  if (n === 1) return `1 ${one}`;
  return `${n} ${many}`;
};

export const shortDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/* Saves a CSV the server built under a readable name. */
export const saveCsv = (blob: Blob, name: string): void => {
  const slug = name.toLowerCase().replace(/[^a-z0-9@.]+/g, '-').replace(/^-+|-+$/g, '') || 'responses';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
